import type { CSSProperties, ReactNode } from 'react'

import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'

export type PartnerOfferLogo = {
  src: string
  alt?: string
  width?: string
  height?: string
}

export type PartnerOffer = {
  id: string
  logo?: PartnerOfferLogo | ReactNode
  badge?: string
  description: ReactNode
  linkPrefix?: ReactNode
  linkText?: ReactNode
  linkSuffix?: ReactNode
  linkHref?: string
}

export type PartnerOffersCarouselProps = {
  offers: PartnerOffer[]
  variant?: 'horizontal' | 'vertical' | 'grid'
  className?: string
}

const cardStyle = {
  width: '100%',
  maxWidth: designTokens.sizes.width.partnerOfferCard,
  background: designTokens.colors.background.surface,
  borderRadius: designTokens.borders.radius.lg,
  borderWidth: designTokens.borders.widths.none,
  borderStyle: designTokens.borders.styles.solid,
  borderColor: designTokens.colors.border.default,
  padding: designTokens.spacing.md,
  gap: designTokens.spacing.partnerOfferHeaderGap,
}

const getCardStyle = (isVertical: boolean, isGrid: boolean) => ({
  ...cardStyle,
  ...(isVertical || isGrid ? { maxWidth: 'none', width: '100%' } : null),
})

const headerRowStyle = {
  gap: designTokens.spacing.xs,
}

const badgeStyle = {
  background: designTokens.colors.background.partnerOfferBadge,
  borderRadius: designTokens.borders.radius.pillLg,
  padding: designTokens.spacing.xs,
  color: designTokens.colors.text.primary,
  whiteSpace: 'nowrap' as const,
  ...typographyStyle(designTokens.typography.badge.sm),
}

const bodyStyle = {
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.baseTight),
}

const sectionTitleStyle = {
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const linkStyle: CSSProperties = {
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.baseTight),
  textDecoration: 'underline',
  textDecorationSkipInk: 'none',
  textUnderlinePosition: 'from-font',
}

const logoStyle = {}

const bodyGroupStyle = {
  gap: designTokens.spacing.lg,
}

const sectionGroupStyle = {
  gap: designTokens.spacing.sm,
}

const isPartnerOfferLogo = (logo: PartnerOfferLogo | ReactNode): logo is PartnerOfferLogo => {
  if (!logo || typeof logo !== 'object') {
    return false
  }
  return 'src' in logo
}

const renderLogo = (logo?: PartnerOfferLogo | ReactNode) => {
  if (!logo) {
    return null
  }

  if (!isPartnerOfferLogo(logo)) {
    return logo
  }

  return (
    <img
      src={logo.src}
      alt={logo.alt ?? ''}
      style={{
        ...(logo.width ? { width: logo.width } : null),
        ...(logo.height ? { height: logo.height } : null),
      }}
    />
  )
}

const PartnerOfferCard = ({
  offer,
  isVertical,
  isGrid,
}: {
  offer: PartnerOffer
  isVertical: boolean
  isGrid: boolean
}) => {
  const showSection = Boolean(offer.linkPrefix || offer.linkText || offer.linkSuffix)

  return (
    <div
      className="flex shrink-0 flex-col items-start"
      style={{
        ...getCardStyle(isVertical, isGrid),
        ...(isGrid ? null : { scrollSnapAlign: 'start' }),
      }}
    >
      <div className="flex w-full flex-wrap items-center justify-between" style={headerRowStyle}>
        <div className="flex items-center" style={logoStyle}>
          {renderLogo(offer.logo)}
        </div>
        {offer.badge ? <div style={badgeStyle}>{offer.badge}</div> : null}
      </div>
      <div className="flex w-full flex-col" style={bodyGroupStyle}>
        <div className="w-full whitespace-pre-wrap" style={bodyStyle}>
          {offer.description}
        </div>
        {showSection ? (
          <div className="flex w-full flex-col whitespace-pre-wrap" style={sectionGroupStyle}>
            <p className="w-full" style={sectionTitleStyle}>
              Как получить:
            </p>
            <p className="w-full" style={bodyStyle}>
              {offer.linkPrefix ?? ''}
              {offer.linkText && offer.linkHref ? (
                <a href={offer.linkHref} style={linkStyle}>
                  {offer.linkText}
                </a>
              ) : (
                (offer.linkText ?? '')
              )}
              {offer.linkSuffix ?? ''}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function PartnerOffersCarousel({
  offers,
  variant = 'horizontal',
  className,
}: PartnerOffersCarouselProps) {
  const isVertical = variant === 'vertical'
  const isGrid = variant === 'grid'
  const isScrollable = !(isVertical || isGrid)

  return (
    <div
      className={cn(
        'w-full',
        isGrid ? 'grid grid-cols-2' : 'flex',
        isVertical ? 'flex-col' : null,
        isScrollable ? 'scrollbar-none overflow-x-auto' : null,
        className
      )}
      style={{
        gap: isGrid ? designTokens.spacing.xxl : designTokens.spacing.lg,
        scrollSnapType: isScrollable ? 'x mandatory' : undefined,
      }}
    >
      {offers.map((offer) => (
        <PartnerOfferCard key={offer.id} offer={offer} isVertical={isVertical} isGrid={isGrid} />
      ))}
    </div>
  )
}
